import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_NODE,
  MIX_AND_MATCH_PRICING_DEALS_NODE,
} from '../../../Constants/Config';
import { createRequestCache } from '../../../utils/requestCache';

const merchantMixMatchRequestCache = createRequestCache({
  ttlMs: 30 * 1000,
  maxEntries: 40,
});

const initialState = {
  loading: false,
  mixAndMatchList: [],
  error: '',
  requestKey: '',
  activeRequestId: null,
  activeRequestKey: '',
};

export const createMixAndMatchRequestKey = (data) => {
  const merchantId = data?.merchant_id;
  if (merchantId === null || merchantId === undefined) return '';

  const normalizedMerchantId = String(merchantId).trim().toUpperCase();
  return normalizedMerchantId ? `merchant:${normalizedMerchantId}` : '';
};

export const fetchMixAndMatchLists = createAsyncThunk(
  'bogoList/fetchMixAndMatchLists',
  async (data, { rejectWithValue, signal }) => {
    const merchantId = String(data?.merchant_id ?? '').trim().toUpperCase();
    if (!merchantId) {
      return rejectWithValue('Missing merchant identifier');
    }

    const requestKey = JSON.stringify([
      BASE_URL_NODE,
      MIX_AND_MATCH_PRICING_DEALS_NODE,
      merchantId,
    ]);

    try {
      return await merchantMixMatchRequestCache.getOrCreate(
        requestKey,
        async () => {
          const response = await axios.post(
            BASE_URL_NODE + MIX_AND_MATCH_PRICING_DEALS_NODE,
            { merchant_id: merchantId },
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          return Array.isArray(response?.data?.data) ? response.data.data : [];
        },
        { forceRefresh: Boolean(data?.forceRefresh), signal },
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || 'Unable to fetch Mix N Match deals',
      );
    }
  },
);

const mixAndMatchListSlice = createSlice({
  name: 'mixAndMatch',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchMixAndMatchLists.pending, (state, action) => {
      const requestId = action.meta?.requestId;
      if (!requestId) return;

      const requestKey = createMixAndMatchRequestKey(action.meta?.arg);
      if (state.requestKey !== requestKey) {
        state.mixAndMatchList = [];
        state.requestKey = '';
      }
      state.loading = true;
      state.error = '';
      state.activeRequestId = requestId;
      state.activeRequestKey = requestKey;
    });
    builder.addCase(fetchMixAndMatchLists.fulfilled, (state, action) => {
      const requestKey = createMixAndMatchRequestKey(action.meta?.arg);
      if (
        action.meta?.requestId !== state.activeRequestId ||
        requestKey !== state.activeRequestKey
      ) {
        return;
      }

      state.loading = false;
      state.mixAndMatchList = Array.isArray(action.payload)
        ? action.payload
        : [];
      state.error = '';
      state.requestKey = requestKey;
      state.activeRequestId = null;
      state.activeRequestKey = '';
    });
    builder.addCase(fetchMixAndMatchLists.rejected, (state, action) => {
      const requestKey = createMixAndMatchRequestKey(action.meta?.arg);
      if (
        action.meta?.requestId !== state.activeRequestId ||
        requestKey !== state.activeRequestKey
      ) {
        return;
      }

      state.loading = false;
      if (!action.meta?.aborted) {
        state.mixAndMatchList = [];
        state.requestKey = '';
        state.error =
          action.payload ||
          action.error?.message ||
          'Unable to load mix-and-match deals';
      }
      state.activeRequestId = null;
      state.activeRequestKey = '';
    });
  },
});

export default mixAndMatchListSlice.reducer;
